// src/modules/Games/Games.jsx
import React, { useEffect, useState, useContext } from 'react';
import Services from '../../services/Services';
import './Games.css';
import { GeneralContext } from '../../GeneralProvider';
import { TbArrowLeft } from "react-icons/tb";
import { useLanguage } from '../../LanguageProvider';

export default function Games() {
    const { getTranslation } = useLanguage();
    const token = localStorage.getItem("token");
    const [games, setGames] = useState([]);
    const [loadingGames, setLoadingGames] = useState(true);
    const [imageStatus, setImageStatus] = useState({}); // { [id]: 'loading' | 'loaded' | 'error' }

    const { selectedGame, startGame, endGame } = useContext(GeneralContext);

    useEffect(() => {
        let isMounted = true;

        Services.GetGames(token)
            .then((res) => {
                if (!isMounted) return;

                if (res?.data?.result && Array.isArray(res.data.result)) {
                    setGames(res.data.result);
                } else {
                    setGames([]);
                }
            })
            .catch((err) => {
                console.error(err);
                if (isMounted) setGames([]);
            })
            .finally(() => {
                if (isMounted) setLoadingGames(false);
            });

        return () => {
            isMounted = false;
        };
    }, [token]);

    const handleGameClick = (game) => {
        startGame(game);
    };

    const handleBack = () => {
        endGame();
    };

    const handleImageLoad = (id) => {
        setImageStatus((prev) => ({
            ...prev,
            [id]: 'loaded',
        }));
    };

    const handleImageError = (id) => {
        setImageStatus((prev) => ({
            ...prev,
            [id]: 'error',
        }));
    };

    const renderSkeletonCards = () => {
        const skeletonCount = 8;
        return Array.from({ length: skeletonCount }).map((_, index) => (
            <div key={index} className="game-card skeleton">
                <div className="game-card-thumb"></div>
                <div className="game-card-title-skeleton"></div>
            </div>
        ));
    };

    return (
        <div className="games-container">
            {!selectedGame && (
                <div className="games-header">
                    <div>
                        <h2 className="games-title">{getTranslation("games")}</h2>
                        <p className="games-subtitle">
                            {getTranslation("gameBreakMessage")}
                        </p>
                    </div>
                </div>
            )}

            {selectedGame ? (
                <div className="game-player-wrapper">
                    <button className="back-icon" onClick={handleBack}>
                        <TbArrowLeft size={20} />
                    </button>

                    <div className="game-player-header">
                        <h3 className="game-player-title">{selectedGame.title}</h3>
                        {selectedGame.description && (
                            <p className="game-player-subtitle">
                                {selectedGame.description}
                            </p>
                        )}
                    </div>

                    <iframe
                        src={selectedGame.url}
                        title={selectedGame.title}
                        frameBorder="0"
                        allowFullScreen
                    ></iframe>
                </div>
            ) : (
                <div className="game-list">
                    {loadingGames
                        ? renderSkeletonCards()
                        : games.map((game) => {
                            const status = imageStatus[game.id];
                            const isLoaded = status === 'loaded';
                            const isError = status === 'error';

                            return (
                                <div
                                    key={game.id}
                                    className="game-card"
                                    onClick={() => handleGameClick(game)}
                                >
                                    <div
                                        className={
                                            "game-card-thumb" +
                                            (!isLoaded && !isError ? " loading" : "")
                                        }
                                    >
                                        {!isError && (
                                            <img
                                                src={game.thumbnail}
                                                alt={game.title}
                                                loading="lazy"
                                                onLoad={() => handleImageLoad(game.id)}
                                                onError={() => handleImageError(game.id)}
                                                style={{
                                                    opacity: isLoaded ? 1 : 0,
                                                }}
                                            />
                                        )}

                                        {isError && (
                                            <div className="game-card-fallback">
                                                <span>
                                                    {game.title
                                                        ? game.title.charAt(0).toUpperCase()
                                                        : "G"}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <p className="game-card-title">
                                        {game.title}
                                    </p>
                                </div>
                            );
                        })}
                    {!loadingGames && games.length === 0 && (
                        <div className="games-empty-state">
                            <p>{getTranslation("noGamesFound")}</p>
                            <span>{getTranslation("checkAgainLater")}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}